"use client";

import { NextPageError } from "@repo/utils/src/errors/next-page-error";
import Link from "next/link";

export default function ErrorBoundary({ error, reset }: {
    error: Error;
    reset?: () => void;
}) {
    let title = "Something went wrong!";
    let message = "Please try again later. If the problem persists, please contact us at support@proportals.com.";
    let action;
    let retryable = false;
    
    //check if it's a ChunkLoadError
    const isChunkError = error?.name === 'ChunkLoadError' || 
                        error?.message?.includes('Loading chunk') ||
                        error?.message?.includes('Loading CSS chunk');
    
    if (isChunkError) {
        title = "Application Update Required";
        message = "The application has been updated. Please refresh the page to get the latest version.";
        retryable = true;
    } else {
        const nextPageError = NextPageError.fromError(error);
        if (nextPageError) {
            title = nextPageError?.title || title;
            message = nextPageError.message || message;
            action = nextPageError.action;
            retryable = nextPageError.retryable || retryable;
        }
    }

    const handleReset = () => {
        if (isChunkError) {
            //for chunk errors, clear cache and reload
            if ('caches' in window) {
                caches.keys().then((names) => {
                    names.forEach((name) => {
                        caches.delete(name);
                    });
                });
            }
        }
        
        if (reset) {
            reset();
        } else {
            window.location.reload();
        }
    };

    return <div className="max-w-2xl m-auto mt-16">
        <div className="text-center px-4">
            <div className="text-3xl font-bold mb-2">{title}</div>
            <div className="mb-6">{message}</div>
            <div className="flex justify-center gap-4">
                {retryable && <button onClick={handleReset} className="btn bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/60 hover:border-gray-300 dark:hover:border-gray-600 text-gray-800 dark:text-gray-300">Try Again</button>}
                {action && <Link href={action.path} className="btn bg-gray-900 text-gray-100 hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-800 dark:hover:bg-white">{action.label}</Link>}
            </div>
        </div>
    </div>;
}
